// SPDX-License-Identifier: MIT
// Seam getUsageBridge() (patron getAiBridge). Desktop = tap live (rate_limit +
// result.usage) + lecteur JSONL Rust ; web = en-têtes anthropic-ratelimit-*.
import type { UsageEvent, WindowState } from './usage-types';
import { WINDOW_DURATION_MS } from './usage-types';
import { computeWindowState } from './usage-meter';
import { parseUsageFromStreamLine } from './usage-stream-tap';
import { parseUsageReport, windowStateFromReport, planLabelFromSubscription } from './usage-report';

export interface UsageBridge {
  // Desktop : à brancher sur AgentRunStartOpts.onLine (chaque ligne NDJSON brute).
  tapLine(line: string): void;
  getCurrentState(): Promise<WindowState>;
}

type Invoke = (cmd: 'read_usage_window', args: { sinceEpochS: number }) => Promise<Array<{
  timestamp_ms: number; input_tokens: number; cache_creation_tokens: number;
  cache_read_tokens: number; output_tokens: number; model: string }>>;

interface DesktopStore {
  getPlanTier(): import('./usage-types').PlanTier;
  getCalibratedCap(): number | null;
  setCalibratedCap?(n: number): void;
  recordThrottle?(ms: number): void;
}

interface DesktopDeps {
  invoke: (cmd: never, args: never) => Promise<unknown>; // remplacé par Tauri invoke en prod
  store: DesktopStore;
  now: () => number;
  // §5 vie privée : le scan JSONL lit ~/.claude/projects de TOUS les projets de
  // l'user (compteurs seuls, jamais le contenu). On ne le lance qu'avec consentement.
  // Le tap rate_limit du run Genesis lui-même n'en a pas besoin (c'est SON run).
  // undefined → pas de gate (back-compat / tests) = consenti.
  consent?: () => boolean;
  // Source AUTORITATIVE : `claude -p "/usage"` (Rust `claude_usage`) → chaîne `result`.
  // Claude donne le % de la fenêtre courante + reset directement → pas de plafond à
  // deviner. Essayée en premier ; fallback sur la reconstruction JSONL si absente/échec.
  usageCommand?: () => Promise<string | null>;
  // Type de forfait (`claude auth status` → subscriptionType : "max"|"pro"). Récupéré
  // UNE fois (stable) puis caché ; affiché dans l'étiquette de la jauge.
  subscriptionType?: () => Promise<string | null>;
}

export function createDesktopUsageBridge(deps: DesktopDeps): UsageBridge {
  const tapped: UsageEvent[] = [];
  let lastReset: number | null = null;
  // Guard against double-firing calibration on the same throttle event.
  let lastThrottleHandled = -1;
  // Type de forfait : fetché une fois (stable), puis caché. undefined = pas encore tenté.
  let cachedPlanLabel: string | null | undefined = undefined;

  return {
    tapLine(line: string) {
      const e = parseUsageFromStreamLine(line, deps.now());
      // JSONL is the sole token source; tapped `usage` events are the same turns
      // re-counted and were always filtered out at merge — so never retain them
      // (they'd accumulate unbounded for the whole session). Keep ONLY rate_limit
      // events (resetsAt anchor + throttle status, absent from JSONL).
      if (!e || e.kind !== 'rate_limit') return;
      tapped.push(e);
      lastReset = e.resetsAt;
    },
    async getCurrentState(): Promise<WindowState> {
      // Source autoritative d'abord : `/usage` donne le % réel + reset (Claude
      // lui-même), gâté par le consentement §5 (lit tes compteurs locaux). Si
      // absent/illisible → fallback sur la reconstruction JSONL ci-dessous.
      const consentedUsage = deps.consent ? deps.consent() : true;
      if (deps.usageCommand && consentedUsage) {
        try {
          const text = await deps.usageCommand();
          if (text) {
            const report = parseUsageReport(text, deps.now());
            if (report) {
              // Type de forfait : fetch une fois puis cache (stable d'une session à l'autre).
              if (cachedPlanLabel === undefined && deps.subscriptionType) {
                try { cachedPlanLabel = planLabelFromSubscription(await deps.subscriptionType()); }
                catch { cachedPlanLabel = null; }
              }
              return windowStateFromReport(report, deps.now(), cachedPlanLabel ?? null);
            }
          }
        } catch { /* dégradation gracieuse §4 → fallback JSONL */ }
      }
      const sinceEpochS = Math.floor(((lastReset ? lastReset * 1000 : deps.now()) - WINDOW_DURATION_MS) / 1000);
      let jsonl: UsageEvent[] = [];
      const consented = deps.consent ? deps.consent() : true;
      try {
        const recs = consented
          ? ((await (deps.invoke as unknown as Invoke)('read_usage_window', { sinceEpochS })) ?? [])
          : [];
        jsonl = recs.map((r) => ({
          kind: 'usage' as const,
          timestampMs: r.timestamp_ms,
          inputTokens: r.input_tokens,
          cacheCreationTokens: r.cache_creation_tokens,
          cacheReadTokens: r.cache_read_tokens,
          outputTokens: r.output_tokens,
          model: r.model,
        }));
      } catch { jsonl = []; } // dégradation gracieuse §4
      // `tapped` now holds rate_limit events only (filtered at tapLine), so no
      // merge-time filter is needed.
      const events = [...jsonl, ...tapped];
      const state = computeWindowState(events, {
        planTier: deps.store.getPlanTier(),
        nowMs: deps.now(),
        // I2: read back the calibrated cap (learned at a real throttle) so
        // fillPct/projection reflect the observed ceiling, not the static seed.
        calibratedCap: deps.store.getCalibratedCap?.() ?? null,
      });

      // guard double-fire within a single now() tick; in prod it re-calibrates each poll while throttled (intended).
      if (state.status === 'throttled' && state.windowTokens > 0) {
        const nowMs = deps.now();
        if (nowMs !== lastThrottleHandled) {
          lastThrottleHandled = nowMs;
          deps.store.setCalibratedCap?.(state.windowTokens);
          deps.store.recordThrottle?.(nowMs);
        }
      }

      return state;
    },
  };
}

// Web : signal pauvre, alimenté par les en-têtes captés par l'edge-fn (Task 9).
export function createWebUsageBridge(getHeaderEvent: () => UsageEvent | null, now: () => number): UsageBridge {
  return {
    tapLine() { /* no-op : pas de stream local sur le web */ },
    async getCurrentState(): Promise<WindowState> {
      const e = getHeaderEvent();
      return computeWindowState(e ? [e] : [], { planTier: 'unknown', nowMs: now(), source: 'web-headers' });
    },
  };
}
